import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Zap,
  Command,
  Search,
  Sun,
  Moon,
  Users,
  FileText,
  MessageSquare,
  BarChart3,
  ClipboardList,
  Cpu,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { AdminUser } from '@/types/admin';
import { AdminErrorBoundary } from './AdminErrorBoundary';
import { buildApiUrl } from '@/config/api.config';
import { logger } from '../../utils/logger';

// ── Types ──────────────────────────────────────────────────────────────────────

interface AdminLayoutProps {
  onLogout: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: LucideIcon;
  permission?: string;
  /** 'arsip' = milik produk saham yang dibekukan 20 Sep 2026; tetap bisa dibuka, dikelompokkan terpisah. */
  group?: 'arsip';
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function isAdminUser(value: unknown): value is AdminUser {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.email === 'string' && typeof obj.role === 'string' && Array.isArray(obj.permissions);
}

// ── Navigation items ───────────────────────────────────────────────────────────

const NAVIGATION: NavigationItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Waitlist', href: '/pengguna/waitlist', icon: ClipboardList, permission: 'users.read' },
  { name: 'Pengguna', href: '/pengguna', icon: Users, permission: 'users.read' },
  { name: 'Support', href: '/support', icon: MessageSquare, permission: 'system.read' },
  { name: 'Sistem', href: '/sistem', icon: BarChart3, permission: 'system.read' },
  { name: 'Pengaturan', href: '/settings', icon: Settings, permission: 'config.write' },
  // Arsip produk saham (keputusan founder 27 Sep 2026): halaman utuh, hanya dikelompokkan.
  { name: 'Sinyal & Saham', href: '/sinyal', icon: Zap, permission: 'signals.read', group: 'arsip' },
  { name: 'Konten', href: '/konten', icon: FileText, permission: 'system.read', group: 'arsip' },
  { name: 'AI & Feature Flags', href: '/sistem-saham', icon: Cpu, permission: 'system.read', group: 'arsip' },
];

/** Item paling spesifik yang cocok dengan path (mis. /pengguna/waitlist -> Waitlist, bukan Pengguna). */
function activeNavItem(pathname: string): NavigationItem | undefined {
  return NAVIGATION.filter(
    (item) => pathname === item.href || (item.href !== '/dashboard' && pathname.startsWith(item.href + '/'))
  ).sort((a, b) => b.href.length - a.href.length)[0];
}

const HEALTH_POLL_MS = 60_000;

// ── Component ──────────────────────────────────────────────────────────────────

const AdminLayout: React.FC<AdminLayoutProps> = ({ onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('admin_theme');
    return saved ? saved === 'dark' : true;
  });
  const [systemStatus, setSystemStatus] = useState<'healthy' | 'degraded' | 'unknown'>('unknown');
  const [searchText, setSearchText] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  // Load admin data from sessionStorage with validation
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('admin_user');
      if (raw) {
        const parsed: unknown = JSON.parse(raw);
        if (isAdminUser(parsed)) {
          setAdmin(parsed);
        } else {
          logger.error('[AdminLayout] Invalid admin_user shape in sessionStorage');
        }
      }
    } catch {
      logger.error('[AdminLayout] Failed to parse admin_user from sessionStorage');
    }
  }, []);

  // Fetch system health status — polls periodically
  const fetchHealth = useCallback(async (signal: AbortSignal) => {
    try {
      const adminToken = sessionStorage.getItem('admin_token');
      if (!adminToken) return;

      const res = await fetch(buildApiUrl('/admin/api/system/health'), {
        headers: { Authorization: `Bearer ${adminToken}` },
        signal,
      });

      if (!res.ok) return;

      const data = (await res.json()) as { overall_status?: string };
      const status = data.overall_status;
      if (status === 'healthy' || status === 'degraded') {
        setSystemStatus(status);
      } else {
        setSystemStatus('unknown');
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      // Silently fail — status stays as-is
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void fetchHealth(controller.signal);

    const id = setInterval(() => void fetchHealth(controller.signal), HEALTH_POLL_MS);
    return () => {
      controller.abort();
      clearInterval(id);
    };
  }, [fetchHealth]);

  // Theme toggle — dispatches CustomEvent for same-tab listeners
  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('admin_theme', newTheme ? 'dark' : 'light');
    window.dispatchEvent(new CustomEvent('theme-change'));
  };

  // Listen for theme changes from other tabs
  useEffect(() => {
    const sync = () => {
      const saved = localStorage.getItem('admin_theme');
      setIsDarkMode(saved ? saved === 'dark' : true);
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  // Escape key to close sidebar on mobile
  useEffect(() => {
    if (!sidebarOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [sidebarOpen]);

  const hasPermission = (permission: string): boolean => {
    return admin?.permissions.includes(permission) ?? false;
  };

  const displayName = admin?.name ?? admin?.email?.split('@')[0] ?? '...';
  const displayEmail = admin?.email ?? '...';
  const displayRole = admin?.role?.replace('_', ' ') ?? 'admin';
  const avatarLetter = displayEmail.charAt(0).toUpperCase();

  // Filter navigation by search + permissions
  const filteredNavigation =
    searchText.trim() === ''
      ? NAVIGATION
      : NAVIGATION.filter((item) => item.name.toLowerCase().includes(searchText.toLowerCase()));

  const handleLogout = () => {
    try {
      sessionStorage.removeItem('admin_user');
      sessionStorage.removeItem('admin_token');
      // Note: admin_theme is NOT cleared — it's a UI preference, not a session credential
      onLogout();
      setTimeout(() => {
        window.location.replace('/login');
      }, 100);
    } catch {
      window.location.reload();
    }
  };

  return (
    <div className={`h-screen flex ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className={`fixed inset-0 z-40 backdrop-blur-sm lg:hidden ${isDarkMode ? 'bg-black/60' : 'bg-black/20'}`}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 w-56 backdrop-blur-xl shadow-2xl transform transition-all duration-300 ease-out lg:relative lg:translate-x-0 h-screen
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isDarkMode ? 'bg-slate-800/95 border-r border-slate-700/50' : 'bg-white/98 border-r border-gray-200/60'}
        `}
      >
        <div className="flex flex-col h-screen">
          {/* Header */}
          <div
            className={`flex items-center justify-between h-16 px-6 ${
              isDarkMode ? 'border-b border-slate-700/50' : 'border-b border-gray-200/50'
            }`}
          >
            <div className="flex items-center space-x-2">
              <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-cyan-500/20' : 'bg-blue-500/20'}`}>
                <Command className={`h-5 w-5 ${isDarkMode ? 'text-cyan-400' : 'text-blue-600'}`} />
              </div>
              <div>
                <h1 className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Admin</h1>
                <p className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>v2.0</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Search */}
          <div className="px-3 py-3">
            <div className="relative">
              <Search
                className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 ${
                  isDarkMode ? 'text-slate-400' : 'text-gray-400'
                }`}
              />
              <input
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className={`w-full rounded-lg pl-8 pr-3 py-2 text-xs transition-all focus:outline-none focus:ring-2 ${
                  isDarkMode
                    ? 'bg-slate-700/50 border border-slate-600/50 text-white placeholder-slate-400 focus:ring-cyan-500/50 focus:border-cyan-500/50'
                    : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-blue-500/50 focus:border-blue-500/50'
                }`}
              />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
            <div className="mb-4">
              <p
                className={`px-3 text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-slate-400' : 'text-gray-500'
                }`}
              >
                Navigation
              </p>
            </div>
            {filteredNavigation.filter((item) => item.group !== 'arsip').map((item) => {
              if (item.permission && !hasPermission(item.permission)) return null;

              const isActive = activeNavItem(location.pathname)?.href === item.href;
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    group relative flex items-center px-2.5 py-2 text-xs font-medium rounded-lg transition-all duration-200
                    ${
                      isActive
                        ? isDarkMode
                          ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                          : 'bg-blue-500/20 text-blue-700 border border-blue-500/30'
                        : isDarkMode
                          ? 'text-slate-300 hover:bg-slate-700/50 hover:text-white border border-transparent'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 border border-transparent'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon
                    className={`h-5 w-5 mr-3 transition-colors ${
                      isActive
                        ? isDarkMode
                          ? 'text-cyan-400'
                          : 'text-blue-600'
                        : isDarkMode
                          ? 'text-slate-400 group-hover:text-slate-300'
                          : 'text-gray-500 group-hover:text-gray-700'
                    }`}
                  />
                  <span className="flex-1">{item.name}</span>
                  {isActive && (
                    <div
                      className={`absolute right-2 h-2 w-2 rounded-full animate-pulse ${
                        isDarkMode ? 'bg-cyan-400' : 'bg-blue-500'
                      }`}
                    />
                  )}
                </Link>
              );
            })}
            {filteredNavigation.some((item) => item.group === 'arsip') && (
              <div className="mt-6 mb-2">
                <p
                  className={`px-3 text-xs font-semibold uppercase tracking-wider ${
                    isDarkMode ? 'text-slate-500' : 'text-gray-400'
                  }`}
                >
                  Arsip Saham
                </p>
                <p className={`px-3 mt-0.5 text-[11px] ${isDarkMode ? 'text-slate-600' : 'text-gray-400'}`}>
                  Dibekukan sejak 20 Sep 2026
                </p>
              </div>
            )}
            <div className="opacity-70">
            {filteredNavigation.filter((item) => item.group === 'arsip').map((item) => {
              if (item.permission && !hasPermission(item.permission)) return null;

              const isActive = activeNavItem(location.pathname)?.href === item.href;
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    group relative flex items-center px-2.5 py-2 text-xs font-medium rounded-lg transition-all duration-200
                    ${
                      isActive
                        ? isDarkMode
                          ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                          : 'bg-blue-500/20 text-blue-700 border border-blue-500/30'
                        : isDarkMode
                          ? 'text-slate-300 hover:bg-slate-700/50 hover:text-white border border-transparent'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 border border-transparent'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon
                    className={`h-5 w-5 mr-3 transition-colors ${
                      isActive
                        ? isDarkMode
                          ? 'text-cyan-400'
                          : 'text-blue-600'
                        : isDarkMode
                          ? 'text-slate-400 group-hover:text-slate-300'
                          : 'text-gray-500 group-hover:text-gray-700'
                    }`}
                  />
                  <span className="flex-1">{item.name}</span>
                  {isActive && (
                    <div
                      className={`absolute right-2 h-2 w-2 rounded-full animate-pulse ${
                        isDarkMode ? 'bg-cyan-400' : 'bg-blue-500'
                      }`}
                    />
                  )}
                </Link>
              );
            })}
            </div>
          </nav>

          {/* Spacer — profile moved to header */}
          <div className="flex-shrink-0 h-4" />
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div
          className={`sticky top-0 z-30 backdrop-blur-xl ${
            isDarkMode
              ? 'bg-slate-800/95 border-b border-slate-700/50'
              : 'bg-white/98 border-b border-gray-200/60'
          }`}
        >
          <div className="flex items-center justify-between h-14 px-6">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Page title */}
            <div className="flex-1 min-w-0 lg:ml-0">
              <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {activeNavItem(location.pathname)?.name ?? 'Dashboard'}
              </h1>
            </div>

            {/* Right side items */}
            <div className="flex items-center space-x-3">
              {/* Notifications shortcut */}
              <button
                onClick={() => navigate('/konten/notifikasi')}
                className={`relative p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                }`}
                title="Notifications"
              >
                <Bell className="h-5 w-5" />
              </button>

              {/* System status */}
              <div
                className={`hidden sm:flex items-center space-x-2 px-3 py-1.5 rounded-lg ${
                  systemStatus === 'healthy'
                    ? isDarkMode
                      ? 'bg-green-500/20 text-green-300'
                      : 'bg-green-100 text-green-700'
                    : systemStatus === 'degraded'
                      ? isDarkMode
                        ? 'bg-yellow-500/20 text-yellow-300'
                        : 'bg-yellow-100 text-yellow-700'
                      : isDarkMode
                        ? 'bg-slate-500/20 text-slate-300'
                        : 'bg-gray-100 text-gray-500'
                }`}
              >
                <div
                  className={`h-1.5 w-1.5 rounded-full animate-pulse ${
                    systemStatus === 'healthy'
                      ? 'bg-green-400'
                      : systemStatus === 'degraded'
                        ? 'bg-yellow-400'
                        : 'bg-gray-400'
                  }`}
                />
                <span className="text-sm font-medium">
                  {systemStatus === 'healthy' ? 'Healthy' : systemStatus === 'degraded' ? 'Degraded' : 'Unknown'}
                </span>
              </div>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                }`}
                title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
              >
                {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>

              {/* User profile */}
              <div className={`hidden sm:flex items-center gap-2.5 pl-3 border-l ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className="relative">
                  <div
                    className={`h-7 w-7 rounded-lg flex items-center justify-center ${
                      isDarkMode
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-500'
                        : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                    }`}
                  >
                    <span className="text-xs font-bold text-white">{avatarLetter}</span>
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 bg-green-400 rounded-full border ${isDarkMode ? 'border-slate-800' : 'border-white'}`} />
                </div>
                <div className="text-left">
                  <p className={`text-xs font-semibold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{displayName}</p>
                  <p className={`text-[10px] leading-tight ${isDarkMode ? 'text-cyan-400' : 'text-blue-600'}`}>{displayRole}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className={`p-1.5 rounded-lg transition-colors ${
                    isDarkMode
                      ? 'text-slate-400 hover:text-red-400 hover:bg-red-500/10'
                      : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                  }`}
                  title="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <main className={`flex-1 p-6 overflow-auto ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50/80'}`}>
          <AdminErrorBoundary>
            <Outlet />
          </AdminErrorBoundary>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
